import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import { BuildingIcon, ChevronDownIcon } from '../../assets/icons/index';
import { QueryKey } from '../../constants/index';
import {
  Company as CompanyHook,
  useCompanyStorageDisplayUrl,
  useInvalidateQueries,
} from '../../hooks/index';
import { companyTypeTranslation } from '../../translations/company-translation';
import { Company, CompanyFilter } from '../../types/company.types';
import { PageRoute } from '../../types/page.types';
import { AppFormattedMessage } from '../app-formatted-message/index';
import { Combobox, ComboboxButton, ComboboxOption, ComboboxOptions } from '../combobox/index';
import { Tooltip } from '../tooltip/index';

type CompanyLogoRoundProps = {
  className: string;
  companyId: string;
  fallbackClassName: string;
  logoImgSrc?: null | string;
};

const CompanyLogoRound: FC<CompanyLogoRoundProps> = ({
  className,
  companyId,
  fallbackClassName,
  logoImgSrc,
}) => {
  const displaySrc = useCompanyStorageDisplayUrl(companyId, logoImgSrc);
  if (displaySrc) {
    return <img alt="" className={className} src={displaySrc} />;
  }
  return <div className={fallbackClassName} />;
};

type CompanySelectProps = {
  className?: string;
  isMobile?: boolean;
  noAdaptive?: boolean;
};

const getSelectorPlaceholderId = (companies: Company[]) => {
  const hasActiveCompany = companies.some((company) => company.isActive);
  return hasActiveCompany ? 'SELECT_ACTIVE_COMPANY' : 'NO_ACTIVE_COMPANIES';
};

const CompanySelect: FC<CompanySelectProps> = ({ noAdaptive = false, className, isMobile }) => {
  const navigate = useNavigate();
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const { selectedCompany } = CompanyHook.useSelected();
  const { companies, isLoading } = CompanyHook.useCompanies({
    filter: CompanyFilter.ACTIVE,
  });

  useEffect(() => {
    if (!selectedCompany) return;

    setSelectedCompanyId(selectedCompany.id);
  }, [selectedCompany]);

  const { invalidateQuery } = useInvalidateQueries(
    QueryKey.GET_COMPANIES,
    QueryKey.GET_SELECTED_COMPANY,
    QueryKey.GET_SCENARIOS,
    QueryKey.GET_USER,
    QueryKey.GET_SCENARIO,
  );

  const { setSelected: setSelectedCompany, isPending } = CompanyHook.useSetSelected();

  const handleSelectCompany = useCallback(
    (value: null | string) => {
      if (value) {
        return setSelectedCompany(value, {
          onSuccess: () => {
            navigate(PageRoute.DASHBOARD);
            invalidateQuery();
          },
        });
      }
    },
    [invalidateQuery, navigate, setSelectedCompany],
  );

  const handleSelect = useCallback(
    (value: null | string) => {
      if (!value) return;
      const company = companies.find((item) => item.id === value);
      if (!company?.isActive) return;
      setSelectedCompanyId(value);
      handleSelectCompany(value);
    },
    [companies, handleSelectCompany],
  );

  const selectorLabel = useMemo(() => {
    if (selectedCompany?.name) return selectedCompany.name;
    if (companies.length === 0) return '';
    return <AppFormattedMessage id={getSelectorPlaceholderId(companies)} />;
  }, [companies, selectedCompany?.name]);

  if (!isLoading && companies.length === 0) {
    return null;
  }

  if (isPending) {
    return (
      <div className="flex h-full w-full min-w-[105px] max-w-[280px] animate-pulse items-center gap-2 rounded-t bg-gray-50 px-2 py-1.5 lg:border-b lg:border-gray-700">
        <div className="size-5 shrink-0 rounded-full bg-gray-200" />
        <div className="h-4 w-full max-w-[140px] rounded bg-gray-200" />
      </div>
    );
  }

  return (
    <>
      {isMobile ? (
        <div className="flex w-full items-center gap-1 overflow-hidden">
          {selectedCompany ? (
            <CompanyLogoRound
              className="size-4 shrink-0 overflow-hidden rounded-full object-cover lg:size-5"
              companyId={selectedCompany.id}
              fallbackClassName="size-5 shrink-0 rounded-full bg-gray-200"
              logoImgSrc={selectedCompany.logoImgSrc}
            />
          ) : (
            <div className="size-5 shrink-0 rounded-full bg-gray-200"></div>
          )}

          <div className="w-full shrink truncate text-start text-sm font-[450] text-gray-700">
            {selectorLabel}
          </div>
        </div>
      ) : (
        <Combobox onChange={handleSelect} value={selectedCompanyId}>
          {({ open, value }) => (
            <div
              className={twMerge(
                'relative w-full min-w-[105px] max-w-[280px] font-inter lg:w-full',
                className,
              )}
            >
              <ComboboxButton
                className={twMerge(
                  'flex h-full w-full items-center justify-between gap-2 rounded-t bg-gray-50 px-2 py-1.5 lg:border-b',
                  open ? 'border-brand-700' : 'border-gray-700',
                )}
              >
                <div className="flex w-full items-center gap-1 overflow-hidden">
                  {selectedCompany ? (
                    <CompanyLogoRound
                      className="size-4 shrink-0 overflow-hidden rounded-full object-cover lg:size-5"
                      companyId={selectedCompany.id}
                      fallbackClassName="size-5 shrink-0 rounded-full bg-gray-200"
                      logoImgSrc={selectedCompany.logoImgSrc}
                    />
                  ) : (
                    <div className="size-5 shrink-0 rounded-full bg-gray-200"></div>
                  )}

                  <div className="w-full shrink truncate text-start text-sm font-[450] text-gray-700">
                    {selectorLabel}
                  </div>
                </div>

                <div className={twMerge('flex items-center gap-2', !noAdaptive && 'max-lg:hidden')}>
                  <div className="max-w-24 truncate rounded-2xl bg-brand-50 px-2 py-[2px] text-label-sm font-[550] text-brand-700 max-lg:hidden">
                    {selectedCompany?.type && (
                      <AppFormattedMessage id={companyTypeTranslation[selectedCompany.type]} />
                    )}
                  </div>
                  <ChevronDownIcon />
                </div>
              </ComboboxButton>
              <ComboboxOptions
                className={
                  'absolute right-0 z-5 flex w-full min-w-[230px] translate-y-[5px] flex-col gap-1.5 rounded bg-white p-2 shadow-xl'
                }
              >
                {companies.map((item) => {
                  const option = (
                    <ComboboxOption
                      className={twMerge(
                        'flex w-full shrink-0 justify-between overflow-hidden rounded px-2 py-[8.5px]',
                        item.isActive
                          ? 'cursor-pointer hover:bg-brand-25'
                          : 'cursor-not-allowed opacity-60',
                        value === item.id ? 'bg-brand-25' : '',
                      )}
                      disabled={!item.isActive}
                      key={`company-${item.id}`}
                      value={item.id}
                    >
                      <div className="flex w-full items-center justify-between gap-1">
                        <div className="flex w-full items-center gap-1.5 overflow-hidden">
                          <CompanyLogoRound
                            className="size-5 shrink-0 rounded-full object-cover"
                            companyId={item.id}
                            fallbackClassName="size-5 shrink-0 rounded-full bg-gray-200"
                            logoImgSrc={item.logoImgSrc}
                          />

                          <div className="truncate text-nowrap text-sm font-normal text-gray-800">
                            {item.name}
                          </div>
                        </div>

                        {item.id === value && (
                          <svg
                            className="h-fit w-fit shrink-0"
                            fill="none"
                            height="17"
                            viewBox="0 0 16 17"
                            width="16"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <circle cx="8" cy="8.5" fill="#2565C8" r="8" />
                            <path
                              d="M11.2725 6.31885L6.77251 10.8188L4.72705 8.77339"
                              stroke="white"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="1.3"
                            />
                          </svg>
                        )}
                      </div>
                    </ComboboxOption>
                  );

                  if (item.isActive) return option;

                  return (
                    <Tooltip
                      content={<AppFormattedMessage id="INACTIVE_COMPANY_CANNOT_SELECT_TOOLTIP" />}
                      key={`company-${item.id}-tooltip`}
                    >
                      {option}
                    </Tooltip>
                  );
                })}

                <div
                  className={twMerge(
                    'h-[1px] w-full bg-gray-200',
                    companies.length < 1 && 'hidden',
                  )}
                />

                <button
                  className="flex items-center gap-1.5 text-nowrap p-2 font-inter text-sm font-[450] text-brand-700"
                  onClick={() => {
                    navigate('/my-companies');
                  }}
                >
                  <BuildingIcon />
                  <div>
                    <AppFormattedMessage id="VIEW_MY_COMPANIES" />
                  </div>
                </button>
              </ComboboxOptions>
            </div>
          )}
        </Combobox>
      )}
    </>
  );
};

export default CompanySelect;
